import { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/common";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Search, Pencil, Video } from "lucide-react";
import VideoUpload from "@/components/VideoUpload";
import { toast } from "sonner";

const asList = (d) => (Array.isArray(d) ? d : d?.items || d?.exercises || d?.results || []);
const LEVELS = ["base", "intermedio", "avanzato"];
const BASE_CATEGORIES = ["autocorrezione", "educazione", "equilibrio", "mobilita", "respirazione", "rinforzo", "stabilizzazione"];
const BASE_DISTRICTS = ["colonna", "cervicale", "dorsale", "lombare", "bacino", "core", "torace", "spalla", "arti_superiori", "arti_inferiori", "catena_posteriore", "globale"];
const empty = { title: "", category: "", district: "", level: "base", objective: "", description: "", instructions: "", sets: "", reps: "", duration_seconds: "", video_url: "" };

export default function ExercisesArchive() {
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Tutti");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try { setExercises(asList(await api.exercises())); }
    catch (e) { setError(errMsg(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (e) => {
    setForm({
      title: e.title || "", category: e.category || "", district: e.district || "", level: e.level || "base",
      objective: e.objective || "", description: e.description || "",
      instructions: Array.isArray(e.instructions) ? e.instructions.join("\n") : (e.instructions || ""),
      sets: e.sets ?? "", reps: e.reps ?? "", duration_seconds: e.duration_seconds ?? "", video_url: e.video_url || "",
    });
    setEditId(e.id || e._id); setOpen(true);
  };

  const save = async () => {
    if (!form.title || !form.description) { toast.error("Titolo e descrizione sono obbligatori"); return; }
    if (!form.duration_seconds || Number(form.duration_seconds) <= 0) { toast.error("La durata (in secondi) è obbligatoria per calcolare i minuti assistenziali"); return; }
    setSaving(true);
    try {
      const body = {
        title: form.title, category: form.category || undefined, district: form.district || undefined,
        level: form.level || undefined, objective: form.objective || undefined, description: form.description,
        instructions: form.instructions ? form.instructions.split("\n").map((s) => s.trim()).filter(Boolean) : undefined,
        sets: form.sets ? Number(form.sets) : undefined, reps: form.reps ? Number(form.reps) : undefined,
        duration_seconds: form.duration_seconds ? Number(form.duration_seconds) : undefined,
        video_url: form.video_url || undefined,
      };
      if (editId) await api.updateExercise(editId, body); else await api.createExercise(body);
      toast.success(editId ? "Esercizio aggiornato" : "Esercizio creato");
      setForm(empty); setEditId(null); setOpen(false); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const categories = [...new Set(exercises.map((e) => e.category).filter(Boolean))];
  const catOptions = [...new Set([...BASE_CATEGORIES, ...categories])].sort();
  const distOptions = [...new Set([...BASE_DISTRICTS, ...exercises.map((e) => e.district).filter(Boolean)])].sort();
  const filtered = exercises.filter((e) =>
    (e.title || e.name || "").toLowerCase().includes(q.toLowerCase()) &&
    (cat === "Tutti" || e.category === cat)
  );

  return (
    <div>
      <PageHeader
        title="Archivio esercizi"
        subtitle="Consulta, crea e modifica esercizi da assegnare ai pazienti."
        action={<Button onClick={openNew} data-testid="create-exercise-button"><Plus className="mr-2 h-4 w-4" /> Nuovo esercizio</Button>}
      />

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca esercizio..." className="pl-9" data-testid="exercise-search" />
      </div>

      <div className="mb-6 flex gap-2 overflow-x-auto pb-1" data-testid="exercise-filters">
        {["Tutti", ...categories].map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`shrink-0 rounded-full border px-4 py-1.5 text-sm font-medium capitalize transition-all ${cat === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-white text-[#1f3a5f] hover:border-primary/40"}`}
            data-testid={`filter-${c.toLowerCase()}`}>{c}</button>
        ))}
      </div>

      {loading ? <Loading /> : error ? <ErrorState message={error} onRetry={load} /> :
        filtered.length === 0 ? <EmptyState title="Nessun esercizio" /> : (
          <div className="space-y-4" data-testid="exercises-list">
            {filtered.map((e, i) => {
              const mins = e.duration_seconds ? Math.round(e.duration_seconds / 60) : null;
              return (
                <Card key={e.id || e._id || i} className="border-border/70 transition-all hover:shadow-md">
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="text-xl font-bold text-[#1f3a5f]">{e.title || e.name}</h3>
                      <Button variant="outline" size="sm" className="shrink-0" onClick={() => openEdit(e)} data-testid={`edit-exercise-${i}`}><Pencil className="mr-1 h-3.5 w-3.5" /> Modifica</Button>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{e.description || e.objective}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {e.video_url && <span className="inline-flex items-center gap-1 rounded-full bg-[#e6f0e9] px-3 py-1 text-xs font-medium text-[#3f7a70]"><Video className="h-3.5 w-3.5" /> Video</span>}
                      {e.level && <span className="rounded-full bg-[#eaf1f6] px-3 py-1 text-xs font-medium capitalize text-primary">{e.level}</span>}
                      {e.category && <span className="rounded-full bg-[#fbe9db] px-3 py-1 text-xs font-medium capitalize text-[#c47f4a]">{e.category}</span>}
                      {e.district && <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium capitalize text-[#1f3a5f]">{String(e.district).replace(/_/g, " ")}</span>}
                      {e.sets && e.reps ? <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-[#1f3a5f]">{e.sets}x{e.reps}</span> : null}
                      {mins ? <span className="rounded-full bg-muted px-3 py-1 text-xs font-medium text-[#1f3a5f]">{mins} min</span> : null}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Modifica esercizio" : "Nuovo esercizio"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5" data-testid="exercise-name-input" /></div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <Label>Categoria</Label>
                <Select value={form.category ?? ""} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 capitalize" data-testid="exercise-category-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{catOptions.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Distretto</Label>
                <Select value={form.district || undefined} onValueChange={(v) => setForm({ ...form, district: v })}>
                  <SelectTrigger className="mt-1.5 capitalize" data-testid="exercise-district-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{distOptions.map((c) => <SelectItem key={c} value={c} className="capitalize">{String(c).replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Livello</Label>
                <Select value={form.level ?? ""} onValueChange={(v) => setForm({ ...form, level: v })}>
                  <SelectTrigger className="mt-1.5 capitalize" data-testid="exercise-level-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent>{LEVELS.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Obiettivo</Label><Input value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Descrizione *</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Serie</Label><Input type="number" value={form.sets} onChange={(e) => setForm({ ...form, sets: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Rip.</Label><Input type="number" value={form.reps} onChange={(e) => setForm({ ...form, reps: e.target.value })} className="mt-1.5" /></div>
              <div><Label>Durata (s) *</Label><Input type="number" value={form.duration_seconds} onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })} className="mt-1.5" data-testid="exercise-duration-input" /></div>
            </div>
            <div><Label>Istruzioni (una per riga)</Label><Textarea rows={4} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} className="mt-1.5" data-testid="exercise-instructions-input" /></div>

            <div className="rounded-xl border border-border/70 bg-muted/30 p-4" data-testid="exercise-video-section">
              <div className="flex items-center gap-2"><Video className="h-4 w-4 text-primary" /><Label className="text-sm font-semibold">Video illustrativo</Label></div>
              <p className="mb-3 mt-1 text-xs text-muted-foreground">Carica un file, usa la fotocamera o registra dalla webcam. Puoi aggiungere il video anche in seguito modificando l'esercizio.</p>
              <VideoUpload value={form.video_url} onChange={(url) => setForm({ ...form, video_url: url })} />
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={saving} data-testid="save-exercise-button">{saving ? "Salvataggio..." : (editId ? "Salva modifiche" : "Crea")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
